import React, { useState, useRef, useEffect } from 'react'
import { HRChatSession } from '@/types/chat.types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MoreHorizontal, Edit, Trash2, Save, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ConversationItemProps {
  session: HRChatSession
  isActive: boolean
  isLoading?: boolean
  onSelect: (sessionId: string) => void
  onRename: (sessionId: string, newTitle: string) => Promise<void>
  onDelete: (sessionId: string) => Promise<void>
}

interface ConfirmDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  isDeleting: boolean
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  isDeleting
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border rounded-lg p-6 w-full max-w-md mx-4 shadow-lg">
        <h3 className="text-lg font-semibold mb-2">Delete Conversation</h3>
        <p className="text-muted-foreground mb-4">
          Are you sure you want to delete "{title}"? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  session,
  isActive,
  isLoading = false,
  onSelect,
  onRename,
  onDelete
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(session.title || '')
  const [isRenaming, setIsRenaming] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleEdit = () => {
    setEditTitle(session.title || '')
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!editTitle.trim()) {
      setEditTitle(session.title || '')
      setIsEditing(false)
      return
    }

    if (editTitle.trim() === session.title) {
      setIsEditing(false)
      return
    }

    setIsRenaming(true)
    try {
      await onRename(session.id, editTitle.trim())
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to rename session:', error)
      setEditTitle(session.title || '')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleCancel = () => {
    setEditTitle(session.title || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(session.id)
      setShowDeleteModal(false)
    } catch (error) {
      console.error('Failed to delete session:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClick = () => {
    if (!isEditing && !isLoading) {
      onSelect(session.id)
    }
  }

  return (
    <TooltipProvider>
      <div
        className={cn(
          'w-full text-left px-3 py-2 text-sm rounded-md transition-colors border border-transparent',
          'flex items-center justify-between group',
          isActive ? 'bg-muted' : '',
          isLoading ? 'bg-primary/10 border-primary/20 border-2 cursor-not-allowed' : 'hover:bg-muted',
          isEditing ? 'ring-2 ring-primary' : ''
        )}
      >
        {/* Main content area */}
        <div 
          className="flex-1 min-w-0 cursor-pointer" 
          onClick={handleClick}
        >
          {isEditing ? (
            <div className="space-y-1">
              <Input
                ref={inputRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-auto py-1 text-sm bg-background"
                disabled={isRenaming}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <div>
              <div className="truncate">
                {session.title || `Chat ${new Date(session.createdAt).toLocaleDateString()}`}
              </div>
              <div className="text-xs text-muted-foreground">
                {session._count?.messages || session.messages?.length || 0} messages
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-1 ml-2">
          {isLoading && (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
          )}

          {isEditing ? (
            <div className="flex items-center space-x-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={isRenaming || !editTitle.trim()}
                    className="h-6 w-6 p-0"
                  >
                    {isRenaming ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Save className="h-3 w-3" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Save (Enter)</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isRenaming}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Cancel (Escape)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
                    isActive && "opacity-100"
                  )}
                  disabled={isLoading}
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setShowDeleteModal(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={session.title || 'this conversation'}
        isDeleting={isDeleting}
      />
    </TooltipProvider>
  )
}

export default ConversationItem